// Reading the codes on a remittance.
//
// `claim_lines.eobs_codes` holds a flat string lifted off the remittance
// advice — "9918 CO:45 572.16- 0351 CO:16" — mixing three code systems and the
// adjustment amounts. Nothing has ever decoded it, so a denial has always
// reached a coordinator as a row of numbers.
//
//   eob    four digits, Georgia-specific   9918 = max fee pricing applied
//   carc   group:code, national X12        CO:45 = charge exceeds fee schedule
//   rarc   letter+digits, national X12     N45   = payment based on authorized amount
//
// The group on a CARC carries real meaning and is worth surfacing: CO is a
// contractual adjustment the provider writes off, PR is patient
// responsibility, OA is another payer's, PI is the payer's own.

/** X12 claim adjustment group codes. */
export const GROUP_CODES = {
  CO: 'Contractual obligation — written off, not billable to the patient',
  PR: 'Patient responsibility',
  OA: 'Other adjustment',
  PI: 'Payer initiated reduction',
};

const AMOUNT = /^-?\$?[\d,]+\.\d{2}-?$/;
const EOB    = /^\d{4}$/;
const CARC   = /^(CO|PR|OA|PI):([A-Z]?\d{1,3}|[A-Z]\d{1,2})$/i;
const RARC   = /^(?:MA|M|N)\d{1,4}$/i;

/**
 * Split a stored eobs_codes string into typed tokens.
 *
 * Unrecognised tokens come back as `kind: 'unknown'` rather than being
 * dropped: code 1072 appears in AHFC's data and in neither DCH reference
 * document, and silently swallowing it would hide that.
 */
export const parseEobsCodes = (raw) => {
  const tokens = [];
  for (const t of String(raw ?? '').split(/[\s,]+/)) {
    const tok = t.trim();
    if (!tok) continue;

    if (AMOUNT.test(tok)) {
      // Trailing minus is how the remittance writes a negative.
      const negative = tok.endsWith('-');
      const value = Number(tok.replace(/[$,-]/g, ''));
      tokens.push({ kind: 'amount', code: tok, amount: negative ? -value : value });
      continue;
    }
    const carc = tok.match(CARC);
    if (carc) {
      tokens.push({ kind: 'carc', code: carc[2].toUpperCase(), group: carc[1].toUpperCase(), raw: tok });
      continue;
    }
    if (EOB.test(tok))  { tokens.push({ kind: 'eob',  code: tok, raw: tok }); continue; }
    if (RARC.test(tok)) { tokens.push({ kind: 'rarc', code: tok.toUpperCase(), raw: tok }); continue; }
    tokens.push({ kind: 'unknown', code: tok, raw: tok });
  }
  return tokens;
};

/** Index eob_codes rows for lookup: `index[kind][code]`. */
export const indexEobCodes = (rows = []) => {
  const index = { eob: {}, carc: {}, rarc: {} };
  for (const r of rows || []) {
    if (index[r?.kind]) index[r.kind][String(r.code).toUpperCase()] = r;
  }
  return index;
};

/**
 * Turn the stored string into lines a person can read.
 *
 * A code with no entry still produces a line saying so, because "we do not
 * have an explanation for 1072" is useful and a blank is not.
 */
export const explainEobs = (raw, index = { eob: {}, carc: {}, rarc: {} }) =>
  parseEobsCodes(raw)
    .filter((t) => t.kind !== 'amount')
    .map((t) => {
      const row = index[t.kind]?.[t.code];
      if (t.kind === 'carc') {
        const group = GROUP_CODES[t.group];
        return {
          ...t,
          description: row?.description || null,
          groupDescription: group || null,
          text: row?.description
            ? `${t.raw} — ${row.description}${group ? ` (${group})` : ''}`
            : `${t.raw} — no description on file`,
        };
      }
      return {
        ...t,
        description: row?.description || null,
        text: row?.description ? `${t.code} — ${row.description}` : `${t.code} — no description on file`,
      };
    });

/** Total of the adjustment amounts in the string. */
export const adjustmentTotal = (raw) =>
  Math.round(parseEobsCodes(raw).filter((t) => t.kind === 'amount')
    .reduce((s, t) => s + t.amount, 0) * 100) / 100;

// ── Keeping the table current ───────────────────────────────────────────────

const LEGEND_EOB  = /EOB CODE DESCRIPTIONS([\s\S]*?)(?=HIPAA REASON CODE|$)/i;
const LEGEND_CARC = /HIPAA ADJ REASON CODE DESCRIPTION([\s\S]*?)(?=HIPAA REMARK CODE|$)/i;
const LEGEND_RARC = /HIPAA REMARK CODE DESCRIPTION([\s\S]*?)$/i;

/**
 * Harvest the code legend printed on a GAMMIS remittance advice.
 *
 * Every RA carries its own EOB legend and the HIPAA reason and remark codes it
 * used, which is both more current and more authoritative than the static
 * reference PDFs — code 1072 is in AHFC's data and in neither of those. The
 * 835 path should run this over each remittance and upsert the result.
 *
 * Returns rows shaped for `eob_codes`. Anything it cannot parse confidently is
 * left out rather than guessed at.
 */
export const harvestRaLegend = (text) => {
  const src = String(text ?? '');
  const rows = [];
  const seen = new Set();
  const add = (kind, code, description) => {
    const key = `${kind}:${code}`;
    const d = String(description || '').replace(/\s+/g, ' ').trim();
    if (!d || d.length < 6 || seen.has(key)) return;
    seen.add(key);
    rows.push({ kind, code, description: d, source: 'remittance advice legend' });
  };

  const eob = src.match(LEGEND_EOB)?.[1] || '';
  // Codes run together on one line: "0274 DESC 0280 DESC ...".
  const eobParts = eob.split(/(?<![\d/-])(\d{4})(?=\s+[A-Z])/);
  for (let i = 1; i < eobParts.length; i += 2) add('eob', eobParts[i], eobParts[i + 1]);

  const carc = src.match(LEGEND_CARC)?.[1] || '';
  const carcParts = carc.split(/(?<![\w./-])([A-Z]?\d{1,3})(?=\s+[A-Z])/);
  for (let i = 1; i < carcParts.length; i += 2) add('carc', carcParts[i], carcParts[i + 1]);

  const rarc = src.match(LEGEND_RARC)?.[1] || '';
  const rarcParts = rarc.split(/(?<![\w./-])((?:MA|M|N)\d{1,4})(?=\s+[A-Z])/);
  for (let i = 1; i < rarcParts.length; i += 2) add('rarc', rarcParts[i], rarcParts[i + 1]);

  return rows;
};
